<script lang="ts">
export default {
  name: 'CompaniesFoodMenusForm',
}
</script>

<script setup lang="ts">
import { useStore } from 'vuex'
import {
  KkButton,
  KkCheckbox,
  KkBox,
  KkTooltip,
  KkImageTooltipContent,
  KkIcon,
  KkLoading,
  KkTextField,
  KkFormLabel,
  KkValidationError,
} from '../../../components/base'
import { KkInfo } from '../../../components/ui'
import { KkFilterModal, KkSelectStore, KkStoreModal } from '../../../components/projects'
import { KkSiteIcon } from '../../../components/base'
import { KkMultiServicePreviewModal, KkItemCounter } from '../../../domains/foodMenu'
import { toNumberLocaleString } from '../../../modules/localeString'
import TextField from '../../../components/TextField.vue'
import MediaImage from '../../../components/MediaImage.vue'
import ValidationError from '../../../components/ValidationError.vue'
import { camelCase } from '../../../modules/string_converter'
import { allSitesConst } from 'src/biz_spa/modules/sitesConst'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type {LocationQueryValue} from 'vue-router'
import { useRoute, useRouter} from 'vue-router'
import { onBeforeRouteLeave } from 'vue-router'
import {useFilterModal} from 'src/biz_spa/composables'
import type {Kind} from 'src/biz_spa/types'
import {useField} from 'vee-validate'
import type { MenuCategory, ServiceType} from 'src/biz_spa/domains/foodMenu/consts'
import { menuCategories, categoryItemMaxLimits, servicesWithItemLimits } from 'src/biz_spa/domains/foodMenu/consts'
import type {FoodMenu, Item, Section } from 'src/biz_spa/domains/foodMenu/types'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import emojiRegex from 'emoji-regex'
import {storeToRefs} from 'pinia'
import {useCompaniesConnectedServices} from 'src/biz_spa/domains/connectedServices/stores'
import {ZodError} from 'zod'

type StoreModalFormValue = {
  stores: string[]
}

type FormKind = Extract<
  Kind,
  | 'brands'
  | 'franchisees'
  | 'areas'
  | 'user_companies'
  | 'prefectures'
  | 'original_tag_group_a'
  | 'original_tag_group_b'
  | 'original_tag_group_c'
  | 'original_tag_group_d'
  | 'original_tag_group_e'
>

type ReadStoreQuery = {
  companyUuid: string
  params: {
    per: number
    page: number
    q: {
      m: 'or'
    }
    search: {
      [key: string]: number[]
    }
  }
}

type SectionFormValues = {
  label: string
  description: string
  item: {
    label: string
    price: number | null
    description: string
    recommend: boolean
    isImageDelete: boolean
    image: {
      id: number | null
      name: string
      content: string | ArrayBuffer | null
      url: string | null
    }
  }
}
type Category = 'food' | 'drink' | 'takeout' | 'lunch'

type FoodMenuRequestParams = {
  foodMenu: Omit<FoodMenu, Category> & { sections: Section[] }
}

const initialSectionFormValues = (): SectionFormValues => ({
  label: '',
  description: '',
  item: {
    label: '',
    price: null,
    description: '',
    recommend: false,
    isImageDelete: false,
    image: {
      id: null,
      name: '',
      content: '',
      url: '',
    },
  }
})

const store = useStore()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
let formChanged = false
const beforeUnloadAction = (event: BeforeUnloadEvent) => {
  if (!formChanged) return

  event.preventDefault()
  event.returnValue = ''
}
onBeforeRouteLeave((to, from) => {
  if (!formChanged) return

  if(confirm(t('pages.CompaniesFoodMenus.edit.leavePageConfirm'))) {
    window.removeEventListener('beforeunload', beforeUnloadAction)
    return true
  }
  return false
})

window.addEventListener('beforeunload', beforeUnloadAction)

const {
  filterItems,
  filterFormValues,
  initFilterItems,
  resetFilterModal,
  toggleFormValue: toggleFilterValues,
} = useFilterModal({ kinds: [
  'brands',
  'franchisees',
  'areas',
  'user_companies',
  'prefectures',
  'original_tag_group_a',
  'original_tag_group_b',
  'original_tag_group_c',
  'original_tag_group_d',
  'original_tag_group_e',
]})

const connectedServicesStore = useCompaniesConnectedServices()
const {
  connectedServices,
  isLoading: connectedServicesLoading,
} = storeToRefs(connectedServicesStore)
const {
  readCompaniesConnectedServices
} = connectedServicesStore

const sectionLabelMaxLength = computed(() => {
  const maxList = selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
      return 140
    case 'tabelog':
      return 40
    case 'gnavi':
      return 45
    case 'hot_pepper_gourmet':
      return 50
    // 特に指定がない場合はDBのカラムの最長だが、65535文字はちょっと長すぎるので200に
    default:
      return 200
    }
  })
  return Math.min(...maxList)
})
const sectionLabelRequired = computed(() => {
  return selectedServiceTypes.value.some((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
      return true
    case 'tabelog':
      return true
    case 'gnavi':
      return false
    case 'hot_pepper_gourmet':
      return false
    default:
      return false
    }
  })
})

const sectionLabelExclusionWords = computed(() => {
  return selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'tabelog':
      return ['http:', 'https:']
    case 'hot_pepper_gourmet':
      return ['ｰ']
    default:
      return []
    }
  }).flat()
})

const sectionLabelSchema = computed(() => {
  const isRequired = sectionLabelRequired.value
  let result = z.string()
  if(sectionLabelExclusionWords.value.length > 0) {
    const regexWords = `^(?:(?!${sectionLabelExclusionWords.value.join('|')}).)*$`
    const regex = new RegExp(regexWords, 's')
    result = result.regex(regex)
  }
  if (sectionLabelMaxLength.value && sectionLabelMaxLength.value > 0) {
    result = result.max(sectionLabelMaxLength.value)
  }
  if (isRequired) {
    return result.nonempty()
  }
  return result
})

// セクション名のuniqueチェックを保存時に行うと必ず引っかかるので、入力時にのみで行えるようにschemaを分けました
const sectionLabelWithUniqueSchema = computed(() => {
  let result = sectionLabelSchema.value
  return result.refine(
    label => {
      return !(sections.value.filter(section => section.key !== activeEdit.value && section.label !== '').some(section => section.label === label))
    },
    {
      message: '同じ名前のセクションが存在します',
    }
  )
})
const sectionLabelRules = computed(() => toTypedSchema(sectionLabelWithUniqueSchema.value))

const sectionLabelFormLabel = (category: MenuCategory) => {
  let label = t('pages.CompaniesFoodMenus.edit.section.label.name')
  const length = t('labels.countCharacters', { count: categoryFormValues.value[category].label.length, max: sectionLabelMaxLength.value })
  label += `(${length})`
  label += sectionLabelRequired.value ? t('labels.requiredLabel') : t('labels.optionalLabel')
  return label
}
const itemLabelMaxLength = computed(() => {
  const maxList = selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
      return 140
    case 'tabelog':
      return 50
    case 'gnavi':
      return 50
    case 'hot_pepper_gourmet':
      return 50
    case 'retty':
      return 40
    // 特に指定がない場合はDBのカラムの最長
    default:
      return 140
    }
  })
  return Math.min(...maxList)
})

const itemLabelExclusionWords = computed(() => {
  return selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'tabelog':
      return ['http:', 'https:']
    case 'hot_pepper_gourmet':
      return ['\\p{Emoji}', 'ｰ', '〜', '麻薬']
    default:
      return []
    }
  }).flat()
})

const emojiValidator = (val: string) => {
  const emojiPattern = emojiRegex()
  return !emojiPattern.test(val)
}

const itemLabelSchema = computed(() => {
  const isRequired = true
  let result = z.string()
  if (isRequired) {
    result = result.nonempty()
  }
  if (itemLabelMaxLength.value > 0) {
    result = result.max(itemLabelMaxLength.value)
  }
  if(itemLabelExclusionWords.value.length > 0) {
    const regexWords = `^(?:(?!${itemLabelExclusionWords.value.join('|')}).)*$`
    const regex = new RegExp(regexWords, 's')
    result = result.regex(regex)

    if(selectedServiceTypes.value.includes('hot_pepper_gourmet')) {
      let refinedResult = result.refine(emojiValidator, {
        message: '絵文字は許可されていません',
      })
      return refinedResult
    }
  }
  return result
})
const itemLabelRules = computed(() => toTypedSchema(itemLabelSchema.value))
const sectionDescriptionExclusionWords = computed(() => {
  return selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'tabelog':
      return ['http:', 'https:']
    default:
      return []
    }
  }).flat()
})
const sectionDescriptionSchema = computed(() => {
  let result = z.string().max(100)
  if(sectionDescriptionExclusionWords.value.length > 0) {
    const regexWords = `^(?:(?!${sectionDescriptionExclusionWords.value.join('|')}).)*$`
    const regex = new RegExp(regexWords, 's')
    result = result.regex(regex)
  }
  return result
})
const sectionDescriptionRules = computed(() => toTypedSchema(sectionDescriptionSchema.value))
const itemDescriptionMaxLength = (item?: SectionFormValues['item']): number => {
  const maxList = selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
      return 1000
    case 'tabelog':
      return 1000
    case 'gnavi':
      return 150
    case 'hot_pepper_gourmet':
      return item && item.recommend ? 150 :200
    case 'retty':
      return 200
    case 'yahoo_place':
      return 330
    default:
      return 0
    }
  }).filter((max) => max > 0)
  if (maxList.length === 0) return 0
  return Math.min(...maxList)
}
const itemDescriptionExclusionWords = computed(() => {
  return selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
      return ['<', '>']
    case 'tabelog':
      return ['http:', 'https:']
    case 'hot_pepper_gourmet':
      return ['\\p{Emoji}', 'ｰ']
    default:
      return []
    }
  }).flat()
})
const itemDescriptionRequired = (item: SectionFormValues['item']) => {
  return selectedServiceTypes.value.includes('retty') && item.recommend
}
const itemDescriptionSchema = (item?: SectionFormValues['item']) => {
  let result = z.string()
  if (itemDescriptionMaxLength(item) > 0) {
    result = result.max(itemDescriptionMaxLength(item))
  }
  if(item && itemDescriptionRequired(item)) {
    result = result.nonempty()
  }
  if(itemDescriptionExclusionWords.value.length > 0) {
    const regexWords = `^(?:(?!${itemDescriptionExclusionWords.value.join('|')}).)*$`
    const regex = new RegExp(regexWords, 's')
    result = result.regex(regex)

    if(selectedServiceTypes.value.includes('hot_pepper_gourmet')) {
      let refinedResult = result.refine(emojiValidator, {
        message: '絵文字は許可されていません',
      })
      return refinedResult
    }
  }

  return result
}
const itemDescriptionRules = computed(() => toTypedSchema(itemDescriptionSchema(activeCategory.value ? categoryFormValues.value[activeCategory.value].item : undefined)))
const itemRecommendMaxLength = computed(() => {
  const maxList = selectedServiceTypes.value.map((serviceType) => {
    switch (serviceType) {
    case 'hot_pepper_gourmet':
      return 3
    case 'retty':
      return 3
    default:
      return 0
    }
  }).filter((max) => max > 0)

  if(maxList.length === 0) return null
  return Math.min(...maxList)
})
const itemPriceRequiredService = computed(() => {
  return selectedServiceTypes.value.some((serviceType) => {
    switch (serviceType) {
    case 'google':
    case 'docomo':
    case 'tabelog':
    case 'retty':
      return false
    case 'gnavi':
    case 'hot_pepper_gourmet':
    case 'yahoo_place':
      return true
    default:
      throw new Error('invalid service type')
    }
  })
})
const itemPriceRequired = (category?: Category) => {
  return itemPriceRequiredService.value || (category && selectedServiceTypes.value.includes('retty') && category === 'takeout')
}
const itemPriceLabel = (value: number, category: Category) => {
  let label = t('pages.CompaniesFoodMenus.edit.item.price.label')
  const length = t('labels.countCharacters', { count: priceCount(value), max: 10 })
  label += `(${length})`
  label += itemPriceRequired(category) ? t('labels.requiredLabel') : t('labels.optionalLabel')
  return label
}
const itemPriceSchema = (category?: Category) => {
  const isRequired = itemPriceRequired(category)
  let result = z.number()
  result = result.max(9999999999)
  if (isRequired) {
    return result.nonnegative()
  } else {
    return result.nullish()
  }
}
const itemPriceRules = computed(() => toTypedSchema(itemPriceSchema(activeCategory.value ? activeCategory.value : undefined)))
const handlePriceChange = (e: Event) => {
  const value = (e.target as HTMLInputElement).valueAsNumber
  return !isNaN(value) ? value : undefined
}

const itemImageRequired = (item: SectionFormValues['item']) => {
  return (selectedServiceTypes.value.includes('retty') || selectedServiceTypes.value.includes('hot_pepper_gourmet')) && item.recommend
}

const isValidItem = (valid: boolean, item: SectionFormValues['item']) => {
  const rettyRecommendValid = itemImageRequired(item) ? item.image.url : true
  return valid && rettyRecommendValid
}

const canSectionAddCategories = ref<MenuCategory[]>([])
const canSectionEditCategories = ref<MenuCategory[]>([])
const canItemAddCategories = ref<MenuCategory[]>([])
const canItemEditCategories = ref<MenuCategory[]>([])
const categoryFormValues = ref({
  food: initialSectionFormValues(),
  lunch: initialSectionFormValues(),
  drink: initialSectionFormValues(),
  takeout: initialSectionFormValues(),
})
const isImageDelete = ref(false)
const activeDropDown = ref('')
const activeEdit = ref('')
const activeCategory = ref<Category | ''>('')
const visibleFilterModal = ref(false)
const showStoreModal = ref(false)
const storeUuids = ref<string[]>([])
const showPreviewModal = ref(false)
const editRecommendItem = ref(false)
const linkedSites = ref<string[]>([])
const showCategoryToolTips = ref<string[]>([])
const showSectionLabelTooltip = ref(false)
const showSectionDescriptionTooltip = ref(false)
const showRecommendTooltip = ref(false)
const showPriceTooltip = ref(false)
const showImageTooltip = ref(false)

const foodMenu = computed<FoodMenu>(() => store.state.companiesFoodMenu.data)
const serverTargetServices = computed<ServiceType[]>(() => store.state.companiesFoodMenu.serverServiceTypes)
const foodMenuLoading = computed(() => store.state.companiesFoodMenu.loading)
const foodMenusFormValues = computed(() => store.state.companiesFoodMenu.formValues)
const stores = computed<{ attributes: { uuid: string, connected_gmb: boolean, external_accounts: [], name: string } }[]>(() => store.state.companiesLocationStores.data)
const filteredCount = computed(() => store.state.companiesLocationStores.filteredCount)
const searchStoresLoading = computed(() => store.state.companiesLocationStores.loading)
const searchAttributesLoading = computed(() => store.state.companiesSearchTagGroups.loading)
const foodMenus = computed<{ id: number; stores: { uuid: string }[] }[]>(() => store.state.companiesFoodMenus.data)
const foodMenusLoading = computed(() => store.state.companiesFoodMenus.loading)
const notAvailable = computed(() => store.state.companiesFoodMenus.notAvailable)
const storeTotalCount = computed(() => store.getters['companiesFoodMenu/storeTotalCount'])
const isDocomoPlan = computed(() => store.getters['companiesCompany/isDocomoPlan'])
const hasDocomoStore = computed(() => store.getters['companiesCompany/hasDocomoStore'])

const { value: foodMenuLabel, errorMessage: foodMenuLabelError } = useField('foodMenuLabel', 'required|max:140', {
  initialValue: foodMenu.value.label,
  label: t('pages.CompaniesFoodMenus.edit.foodMenu.title')
})

const selectedServiceTypes = computed<ServiceType[]>(() => store.state.companiesFoodMenu.data.targetServices)
const selectedTabelog = computed<boolean>(() => selectedServiceTypes.value.includes('tabelog'))
const categoryTooltipText = computed(() => {
  const isSelectOnlyGoogle = selectedServiceTypes.value.length === 1 && selectedServiceTypes.value[0] === 'google'
  if (isSelectOnlyGoogle) {
    return t('pages.CompaniesFoodMenus.edit.category.tooltip.descriptionOnlyGoogle')
  }
  return t('pages.CompaniesFoodMenus.edit.category.tooltip.description')
})
const sectionLabelNotAvailableServices = computed(() => ['retty'])
const showSectionLabelForm = computed(() => selectedServiceTypes.value.filter(v => !sectionLabelNotAvailableServices.value.includes(v)).length > 0)
const sectionLabelAvailableSelectedServices = computed(() => selectedServiceTypes.value.filter(site => !sectionLabelNotAvailableServices.value.includes(site)))
const sectionDescriptionAvailableServices = computed(() => ['tabelog', 'gnavi'])
const showSectionDescriptionForm = computed(() => selectedServiceTypes.value.filter(v => sectionDescriptionAvailableServices.value.includes(v)).length > 0)
const sectionDescriptionAvailableSelectedServices = computed(() => selectedServiceTypes.value.filter(site => sectionDescriptionAvailableServices.value.includes(site)))
const imageAvailableServices = computed(() => ['tabelog', 'gnavi', 'hot_pepper_gourmet', 'retty', 'yahoo_place'])
const imageAvailableSelectedServices = computed(() => selectedServiceTypes.value.filter(site => imageAvailableServices.value.includes(site)))
const showImageForm = computed(() => imageAvailableSelectedServices.value.length > 0)
const availableSites = computed<typeof allSitesConst[number][]>(() => {
  // 一旦、食べログは特定会社のみ表示する
  const availableServices: ServiceType[] = ['google', 'tabelog', 'retty', 'gnavi', 'hot_pepper_gourmet', 'yahoo_place']
  const available: ServiceType[] = isDocomoPlan.value ? ['docomo'] : availableServices.filter(item => connectedServices.value.serviceTypes.includes(item))
  if(hasDocomoStore.value) available.push('docomo')
  return allSitesConst.filter(site => available.includes(site.siteType))
})
const isServiceTypeSelected = computed(() => selectedServiceTypes.value.length > 0)

const recommendAvailableSelectedServices = computed(() => {
  return selectedServiceTypes.value.filter(site => !['google', 'yahoo_place'].includes(site))
})

const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const currentId = computed(() => route.params.id as LocationQueryValue)
const copyMenuId = computed(() => route.query.copy as LocationQueryValue)
const loading = computed(() => foodMenuLoading.value || foodMenusLoading.value || searchStoresLoading.value || searchAttributesLoading.value || connectedServicesLoading.value)
const invalidFoodMenu = computed(() => {
  return !foodMenuLabel.value || sections.value.length === 0 || sections.value.some(section => section.items.length === 0)
})
const readStoreQuery = computed<ReadStoreQuery>(() => {
  return {
    companyUuid: currentCompanyUuid.value,
    params: {
      page: 1,
      per: 9999,
      q: {
        m: 'or'
      },
      search: {
        brand_ids: filterFormValues.value.brands,
        area_ids: filterFormValues.value.areas,
        franchisee_ids: filterFormValues.value.franchisees,
        user_company_ids: filterFormValues.value.user_companies,
        prefectures: filterFormValues.value.prefectures,
        original_tag_group_a_tag_ids: filterFormValues.value.original_tag_group_a,
        original_tag_group_b_tag_ids: filterFormValues.value.original_tag_group_b,
        original_tag_group_c_tag_ids: filterFormValues.value.original_tag_group_c,
        original_tag_group_d_tag_ids: filterFormValues.value.original_tag_group_d,
        original_tag_group_e_tag_ids: filterFormValues.value.original_tag_group_e,
      }
    },
  }
})
const storeNames = computed(() => {
  return stores.value
    .filter(store => storeUuids.value.includes(store.attributes.uuid))
    .map(store => store.attributes.name)
})
const foodMenuSections = computed(() => {
  return menuCategories.map(category => {
    return categorySections(category)
  }).flat()
})
// アイテム上限が決まっているサービスを選択している場合のみ上限カウンターを表示させる
const showItemCounter = computed<boolean>(() => {
  return selectedServiceTypes.value.some((service: ServiceType) => servicesWithItemLimits.includes(service))
})

const readFoodMenu =  (id: string) => store.dispatch('companiesFoodMenu/readFoodMenu', id)
const duplicateFoodMenu =  (id: string) => store.dispatch('companiesFoodMenu/duplicateFoodMenu', id)
const readStores = (payload: ReadStoreQuery): Promise<void> => store.dispatch('companiesLocationStores/readStores', payload)
const readSearchAttributes = (companyUuid: string): Promise<void> => store.dispatch('companiesSearchTagGroups/readSearchTagGroups', companyUuid)
const createFoodMenu = (payload: FoodMenuRequestParams) => store.dispatch('companiesFoodMenu/createFoodMenu', payload)
const updateFoodMenu = (payload: { id: string, payload: FoodMenuRequestParams }) => store.dispatch('companiesFoodMenu/updateFoodMenu', payload)
const uploadFoodMenuItemImage = (image: File) => store.dispatch('companiesFoodMenu/uploadFoodMenuItemImage', image)
const deleteFoodMenuItemImage = (imageId: number | null) => store.dispatch('companiesFoodMenu/deleteFoodMenuItemImage', imageId)
const readFoodMenus = () => store.dispatch('companiesFoodMenus/readFoodMenus')

const addSection = (payload: { sectionData: Omit<Section, 'key' | 'items'> & { items: Omit<Item, 'key'>[] } }) => store.commit('companiesFoodMenu/addSection', payload)
const updateSection = (payload: Pick<Section, 'label' | 'description' | 'category' | 'key'>) => store.commit('companiesFoodMenu/updateSection', payload)
const deleteSection = (payload: Pick<Section, 'category' | 'key'>) => store.commit('companiesFoodMenu/deleteSection', payload)
const addItem = (payload: { item: Omit<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/addItem', payload)
const updateItem = (payload: { item: Item, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/updateItem', payload)
const deleteItem = (payload: { item: Pick<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/deleteItem', payload)
const moveSectionTop = (payload: { key: string, category: MenuCategory }) => store.commit('companiesFoodMenu/moveSectionTop', payload)
const moveSectionBottom = (payload: { key: string, category: MenuCategory }) => store.commit('companiesFoodMenu/moveSectionBottom', payload)
const moveSectionOneUp = (payload: { key: string, category: MenuCategory }) => store.commit('companiesFoodMenu/moveSectionOneUp', payload)
const moveSectionOneDown = (payload: { key: string, category: MenuCategory }) => store.commit('companiesFoodMenu/moveSectionOneDown', payload)
const moveItemTop = (payload: { item: Pick<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/moveItemTop', payload)
const moveItemBottom = (payload: { item: Pick<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/moveItemBottom', payload)
const moveItemOneUp = (payload: { item: Pick<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/moveItemOneUp', payload)
const moveItemOneDown = (payload: { item: Pick<Item, 'key'>, section: Pick<Section, 'category' | 'key'> }) => store.commit('companiesFoodMenu/moveItemOneDown', payload)
const initNewFoodMenu = () => store.commit('companiesFoodMenu/initNewFoodMenu')
const setFoodMenuFormValues = (payload: StoreModalFormValue) => store.commit('companiesFoodMenu/setFormValues', payload)
const toggleFormValue = (payload: { allChecked: boolean; initialValues: string[]; target: 'stores' }) => store.commit('companiesFoodMenu/toggleFilterValues', payload)
const setFoodMenuFilterValues = (payload: { target: 'stores'; value: string }) => store.commit('companiesFoodMenu/setFilterValues', payload)
const setTargetServices = (payload: ServiceType[]) => store.commit('companiesFoodMenu/setTargetServices', payload)

const categoryAvailableSelectedServices = (category: MenuCategory) => {
  switch (category) {
  case 'food':
    return selectedServiceTypes.value.filter(site => !['google'].includes(site))
  case 'lunch':
    return selectedServiceTypes.value.filter(site => !['google', 'yahoo_place'].includes(site))
  case 'drink':
    return selectedServiceTypes.value.filter(site => !['google'].includes(site))
  case 'takeout':
    return selectedServiceTypes.value.filter(site => !['google', 'tabelog', 'yahoo_place'].includes(site))
  default:
    return []
  }
}
const images = (category: MenuCategory) => {
  return categoryFormValues.value[category]?.item.image.url ? [categoryFormValues.value[category].item.image] : []
}
const invalidRecommendItemEdit = computed(() => {
  const recommendItems = sections.value.map(section => section.items.filter(item => item.isRecommend)).flat()

  if(itemRecommendMaxLength.value === null) return false
  return !editRecommendItem.value && recommendItems.length >= itemRecommendMaxLength.value
})
const invalidRecommend = computed(() => {
  const recommendItems = sections.value.map(section => section.items.filter(item => item.isRecommend)).flat()

  if(itemRecommendMaxLength.value === null) return false
  return recommendItems.length > itemRecommendMaxLength.value
})
const categorySections = (category: MenuCategory) => {
  const sections = foodMenu.value[category]?.sections || []
  sections.forEach(section => {
    return section.items.sort((a, b) => {
      if (a.sortOrder == null || b.sortOrder == null) return 0
      if (a.sortOrder < b.sortOrder) return -1
      if (a.sortOrder > b.sortOrder) return 1
      return 0
    })
  })
  return sections.sort((a, b) => {
    if (a.sortOrder == null || b.sortOrder == null) return 0
    if (a.sortOrder < b.sortOrder) return -1
    if (a.sortOrder > b.sortOrder) return 1
    return 0
  })
}

const sections = computed(() => {
  return menuCategories.map(category => categorySections(category)).flat()
})

const availableImageSize = computed(() => {
  return selectedServiceTypes.value.includes('yahoo_place') ? '720' : '400'
})

const handleSiteTypeChange = (value: ServiceType) => {
  const newValues = [...selectedServiceTypes.value]
  newValues.includes(value)
    ? newValues.splice(newValues.indexOf(value), 1)
    : newValues.push(value)
  setTargetServices(newValues)
}
const handleCloseFilterModal = () => {
  setFoodMenuFormValues({ stores: [...storeUuids.value] })
  visibleFilterModal.value = false
}
const handleOpenStoreModal = () => {
  if (storeUuids.value.length !== 0) {
    showStoreModal.value = true
  } else {
    visibleFilterModal.value = true
  }
}

const gnaviMaxItemCount = computed(() => categoryItemMaxLimits['gnavi'] as number)
const tabelogMaxItemCount = computed(() => categoryItemMaxLimits['tabelog'] as number)
const hotPepperMaxItemCount = computed(() => categoryItemMaxLimits['hot_pepper_gourmet'] as number)

const calcTotalMenuItemCountWithoutSectionDescription = (category: Category) => {
  const targetSections = categorySections(category)
  const itemCount = targetSections.map(section => section.items.length).reduce((a, b) => a + b, 0)
  const sectionLabelCount = targetSections.filter(s => s.label).length
  return itemCount + sectionLabelCount
}
const calcTotalMenuItemCount = (category: Category) => {
  const targetSections = categorySections(category)
  const sectionDescriptionCount = targetSections.filter(s => s.description).length
  return calcTotalMenuItemCountWithoutSectionDescription(category) + sectionDescriptionCount
}
const handleAddSection = (category: MenuCategory) => {
  formChanged = true
  const sectionData = {
    category,
    label: categoryFormValues.value[category].label,
    description: categoryFormValues.value[category].description || '',
    items: [
      {
        label: categoryFormValues.value[category].item.label,
        price: categoryFormValues.value[category].item.price,
        description: categoryFormValues.value[category].item.description,
        isRecommend: categoryFormValues.value[category].item.recommend,
        imageId: categoryFormValues.value[category].item.image.id,
        filename: categoryFormValues.value[category].item.image.name,
        content: categoryFormValues.value[category].item.image.content,
        imageUrl: categoryFormValues.value[category].item.image.url,
        sortOrder: 1,
        isImageDelete: categoryFormValues.value[category].item.isImageDelete
      }
    ]
  }
  addSection({ sectionData })
  initialEdit(category)
}
const handleUpdateSection = (key: string, category: MenuCategory) => {
  formChanged = true
  updateSection({
    key,
    label: categoryFormValues.value[category].label,
    description: categoryFormValues.value[category].description || '',
    category
  })
  initialEdit(category)
}


const handleAddItem = (sectionKey: string, category: MenuCategory) => {
  formChanged = true
  const itemData = {
    label: categoryFormValues.value[category].item.label,
    price: categoryFormValues.value[category].item.price,
    description: categoryFormValues.value[category].item.description,
    isRecommend: categoryFormValues.value[category].item.recommend,
    imageId: categoryFormValues.value[category].item.image.id,
    filename: categoryFormValues.value[category].item.image.name,
    content: categoryFormValues.value[category].item.image.content,
    imageUrl: categoryFormValues.value[category].item.image.url,
    isImageDelete: categoryFormValues.value[category].item.isImageDelete
  }
  addItem({ section: { key: sectionKey, category }, item: itemData })
  initialEdit(category)
}

const handleUpdateItem = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  formChanged = true
  const item = {
    label: categoryFormValues.value[category].item.label,
    price: categoryFormValues.value[category].item.price,
    description: categoryFormValues.value[category].item.description,
    isRecommend: categoryFormValues.value[category].item.recommend,
    imageId: categoryFormValues.value[category].item.image.id,
    filename: categoryFormValues.value[category].item.image.name,
    content: categoryFormValues.value[category].item.image.content,
    imageUrl: categoryFormValues.value[category].item.image.url,
    isImageDelete: categoryFormValues.value[category].item.isImageDelete,
    key: itemKey,
  }
  updateItem({
    section: {
      key: sectionKey,
      category
    },
    item
  })
  initialEdit(category)
}

const handleDeleteSection = (key: string, category: MenuCategory) => {
  formChanged = true
  deleteSection({ key, category })
  closeDropdown()
}

const handleDeleteItem = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  formChanged = true
  deleteItem({
    section: {
      key: sectionKey,
      category
    },
    item: {
      key: itemKey
    }
  })
  closeDropdown()
}

const saveFoodMenus = async () => {
  const payload: FoodMenuRequestParams = {
    foodMenu: {
      label: foodMenuLabel.value,
      storeUuids: storeUuids.value,
      targetServices: selectedServiceTypes.value,
      sections: menuCategories.map(category => {
        return categorySections(category)
          .map(section => {
            if (copyMenuId.value) {
              delete section.id
            }
            return {
              ...section,
              items: section.items.map(item => {
                if (copyMenuId.value) {
                  delete item.id
                }
                item.content = null
                return {
                  ...item,
                }
              })
            }
          })
      }).flat(),
    }
  }

  let otherFoodMenusStoreUuids: string[] = []
  foodMenus.value.filter(v => v.id !== Number(currentId.value)).forEach(foodMenu => {
    otherFoodMenusStoreUuids = otherFoodMenusStoreUuids.concat(foodMenu.stores.map(s => s.uuid))
  })

  if (storeUuids.value.some(uuid => otherFoodMenusStoreUuids.includes(uuid))) {
    if (!confirm(t('pages.CompaniesFoodMenus.edit.labels.duplicateStore.confirm'))) {
      return
    }
  }

  // 更新時のみ、サービスや店舗のチェックがはずされていらアラート
  if (currentId.value) {
    const isStoreRemoved = foodMenu.value.storeUuids.some(uuid => !storeUuids.value.includes(uuid))
    const isServiceRemoved = serverTargetServices.value.some(service => !selectedServiceTypes.value.includes(service))
    if (isServiceRemoved || isStoreRemoved) {
      if (!confirm(t('pages.CompaniesFoodMenus.deleteNotice'))) {
        return
      }
    }
  }

  showPreviewModal.value = false
  let isSuccess
  if (currentId.value) {
    isSuccess = await updateFoodMenu({ id: currentId.value, payload: payload })
  } else {
    isSuccess = await createFoodMenu(payload)
  }

  if (isSuccess) {
    formChanged = false
    router.push({ name: 'CompaniesFoodMenusIndex', params: { company_uuid: currentCompanyUuid.value } })
  }
}

const validateItemCount = (category: Category) => {
  if (selectedServiceTypes.value.includes('gnavi') && calcTotalMenuItemCount(category) > gnaviMaxItemCount.value) {
    throw new Error(t('pages.CompaniesFoodMenus.edit.validation.gnaviTotalCount'))
  }
  if (selectedServiceTypes.value.includes('tabelog') && calcTotalMenuItemCountWithoutSectionDescription(category) > tabelogMaxItemCount.value) {
    throw new Error(t('pages.CompaniesFoodMenus.edit.validation.tabelogTotalCount'))
  }
  if (selectedServiceTypes.value.includes('hot_pepper_gourmet') && calcTotalMenuItemCountWithoutSectionDescription(category) > hotPepperMaxItemCount.value) {
    throw new Error(t('pages.CompaniesFoodMenus.edit.validation.hotPepperTotalCount'))
  }
}

const openAddItem = (key: string, category: MenuCategory) => {
  initialEdit(category)
  canItemAddCategories.value.push(category)
  activeEdit.value = key
  activeCategory.value = category
}

const openAddSection = (category: MenuCategory) => {
  initialEdit(category)
  canSectionAddCategories.value.push(category)
  activeCategory.value = category
}

const openEditSection = (section: Section, category: MenuCategory) => {
  initialEdit(category)
  categoryFormValues.value[category].label = section.label
  categoryFormValues.value[category].description = section.description || ''
  activeEdit.value = section.key
  activeCategory.value = category
  canSectionEditCategories.value.push(category)
}

const openEditItem = (item: Item, category: MenuCategory) => {
  initialEdit(category)
  categoryFormValues.value[category].item.label = item.label
  categoryFormValues.value[category].item.price = item.price
  categoryFormValues.value[category].item.description = item.description || ''
  categoryFormValues.value[category].item.recommend = item.isRecommend
  categoryFormValues.value[category].item.image = {
    id: item.imageId,
    content: item.content,
    name: item.filename,
    url: item.imageUrl
  }
  activeEdit.value = item.key
  activeCategory.value = category
  editRecommendItem.value = item.isRecommend
  canItemEditCategories.value.push(category)
  isImageDelete.value = item.isImageDelete
}

const openPreviewModal = () => {
  try {
    if(invalidRecommend.value) throw new Error()
    sections.value.forEach(section => {
      sectionLabelSchema.value.parse(section.label)
      if (section.description) {
        sectionDescriptionSchema.value.parse(section.description)
      }
      section.items.forEach(item => {
        const sectionFormItem: SectionFormValues['item'] = { label: item.label, price: item.price, description: item.description, recommend: item.isRecommend, isImageDelete: item.isImageDelete, image: { id: item.imageId, name: item.filename, content: item.content, url: item.imageUrl } }
        itemLabelSchema.value.parse(item.label)
        itemDescriptionSchema(sectionFormItem).parse(item.description)
        itemPriceSchema(section.category).parse(item.price)
        if(!isValidItem(true, sectionFormItem)) throw new Error()
      })
    })
    menuCategories.forEach(category => validateItemCount(category))
  } catch (e: unknown) {
    if (e instanceof ZodError) {
      alert(t('messages.invalid'))
      return
    }
    if (e instanceof Error) {
      alert(e.message || t('messages.invalid'))
      return
    }
  }

  const targetStores = stores.value.filter(store => storeUuids.value.includes(store.attributes.uuid))
  const sites = targetStores.find(store => store.attributes.connected_gmb) ? ['google'] : []
  const externalAccountSites = targetStores.flatMap(store => store.attributes.external_accounts)
  linkedSites.value = sites.concat(externalAccountSites).map(site => camelCase(site))
  showPreviewModal.value = true
}

const cancelPage = () => {
  if (confirm(t('labels.cancelInput'))) {
    router.push({ name: 'CompaniesFoodMenusIndex', params: { company_uuid: currentCompanyUuid.value } })
  }
}

const initialEdit = (category: MenuCategory) => {
  canSectionAddCategories.value = []
  canSectionEditCategories.value = []
  canItemAddCategories.value = []
  canItemEditCategories.value = []
  activeDropDown.value = ''
  categoryFormValues.value[category] = initialSectionFormValues()
  editRecommendItem.value = false
  isImageDelete.value = false
  activeCategory.value = ''
}

const openDropdown = (key: string) => {
  activeDropDown.value = activeDropDown.value === key ? '' : key
}

const closeDropdown = () => {
  activeDropDown.value = ''
}

const handleChangeFormPosition = () => {
  formChanged = true
  activeDropDown.value = ''
}

const handleMoveSectionTop = (key: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveSectionTop({ key, category })
}

const handleMoveSectionOneUp = (key: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveSectionOneUp({ key, category })
}

const handleMoveSectionOneDown = (key: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveSectionOneDown({ key, category })
}

const handleMoveSectionBottom = (key: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveSectionBottom({ key, category })
}

const handleMoveItemTop = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveItemTop({
    section: {
      key: sectionKey,
      category
    },
    item: {
      key: itemKey
    }
  })
}

const handleMoveItemOneUp = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveItemOneUp({
    section: {
      key: sectionKey,
      category
    },
    item: {
      key: itemKey
    }
  })
}

const handleMoveItemOneDown = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveItemOneDown({
    section: {
      key: sectionKey,
      category
    },
    item: {
      key: itemKey
    }
  })
}

const handleMoveItemBottom = (sectionKey: string, itemKey: string, category: MenuCategory) => {
  handleChangeFormPosition()
  moveItemBottom({
    section: {
      key: sectionKey,
      category
    },
    item: {
      key: itemKey
    }
  })
}

const handleSubmitFilterModal = () => {
  visibleFilterModal.value = false
  showStoreModal.value = true
}

const handleResetFilterModal = () => {
  resetFilterModal()
  readStores(readStoreQuery.value)
}

const handleBack = () => {
  showStoreModal.value = false
  visibleFilterModal.value = true
}

const onDropMediaImage = async (images: File[], category: MenuCategory) => {
  if(loading.value) return
  const res = await uploadFoodMenuItemImage(images?.[0])
  categoryFormValues.value[category].item.image = {
    id: res.id,
    name: res.name,
    content: res.content,
    url: res.url,
  }
}

const onRemoveMediaImage = async (category: MenuCategory) => {
  await deleteFoodMenuItemImage(categoryFormValues.value[category].item.image.id)
  categoryFormValues.value[category].item.image = {
    id: null,
    name: '',
    content: '',
    url: '',
  }
  categoryFormValues.value[category].item.isImageDelete = true
}

const confirmStoreUuids = (uuids: string[]) => {
  showStoreModal.value = false
  storeUuids.value = uuids
}

const numberLocaleString = (value: number) => {
  return toNumberLocaleString(value)
}

const handleChangeFilterModalCheckBox = ({ kind, id }: { kind: FormKind, id: number }) => {
  toggleFilterValues({ kind, id })

  readStores(readStoreQuery.value)
}

const priceCount = (price: number) => {
  return price ? price.toString().length : 0
}

const isSelectedHPG = () => selectedServiceTypes.value.includes('hot_pepper_gourmet')

const init = async() => {
  initNewFoodMenu()
  if(currentId.value) {
    readFoodMenu(currentId.value).then(() =>{
      storeUuids.value = foodMenu.value.storeUuids || []
      foodMenuLabel.value = foodMenu.value.label
    })
  } else if(copyMenuId.value){
    duplicateFoodMenu(copyMenuId.value).then(() => {
      storeUuids.value = foodMenu.value.storeUuids || []
      foodMenuLabel.value = `${ foodMenu.value.label }のコピー`
    })
  }
  readFoodMenus()
  readCompaniesConnectedServices({companyUuid: currentCompanyUuid.value})
  await readSearchAttributes(currentCompanyUuid.value)
  await resetFilterModal()
  initFilterItems()
  readStores(readStoreQuery.value)
  if(isDocomoPlan.value) {
    setTargetServices(['docomo'])
  }
}
init()
</script>

<template>
  <div v-if="!notAvailable" class="foodMenu">
    <div class="p-box">
      <div class="p-box__header">
        <div class="foodMenuIndexLink">
          <router-link
            :to="{ name: 'CompaniesFoodMenusIndex', params: { company_uuid: currentCompanyUuid } }"
            class="c-button__item c-button__item--outline"
            v-text="t('pages.CompaniesFoodMenus.links.index')"
          />
        </div>
      </div>
      <div v-if="!loading" class="p-box__body">
        <div class="c-grid__items">
          <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__8 c-grid__item__l__8">
            <div class="p-box">
              <div class="p-box__body">
                <h3 class="foodMenu__title">
                  {{ t('pages.CompaniesFoodMenus.edit.foodMenu.title') }}
                </h3>
                <KkInfo>
                  <p>{{ t('pages.CompaniesFoodMenus.edit.foodMenu.description1') }}</p>
                  <p>{{ t('pages.CompaniesFoodMenus.edit.foodMenu.description2') }}</p>
                </KkInfo>
                <fieldset class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                  <KkFormLabel
                    :text="t('pages.CompaniesFoodMenus.edit.foodMenu.title') + '(' + t('labels.countCharacters', { count: foodMenuLabel.length, max: 140 }) + ')' + t('labels.requiredLabel')"
                  />
                  <KkTextField v-model="foodMenuLabel" />
                  <KkValidationError :text="foodMenuLabelError || ''" />
                </fieldset>
                <span class="foodMenu__example">{{ t('pages.CompaniesFoodMenus.edit.foodMenu.example') }}</span>
              </div>
            </div>
            <KkBox
              v-if="!isDocomoPlan"
              :title="t('pages.CompaniesFoodMenus.edit.service.title')"
              border
              class="selectServices"
            >
              <template #content>
                <KkInfo>
                  <p>{{ t('pages.CompaniesFoodMenus.edit.service.description') }}</p>
                </KkInfo>
                <KkInfo v-if="isSelectedHPG()">
                  <p v-html="$t('pages.CompaniesFoodMenus.edit.service.hotPepperGourmetDescription')" />
                </KkInfo>
                <div class="selectServices__form">
                  <KkFormLabel :text="t('pages.CompaniesFoodMenus.edit.service.label')" />
                  <div class="serviceCheckbox">
                    <KkCheckbox
                      v-for="site in availableSites"
                      :id="site.siteType"
                      :key="site.siteType"
                      :label="site.name"
                      :model-value="selectedServiceTypes.includes(site.siteType)"
                      @update:modelValue="handleSiteTypeChange(site.siteType)"
                    />
                  </div>
                </div>
              </template>
            </KkBox>
            <div v-if="isServiceTypeSelected" class="categories">
              <KkBox
                v-for="category in menuCategories"
                :key="category"
                :header-bottom-border="false"
                border
              >
                <template #content>
                  <div class="categoryHeader">
                    <h2 class="categoryHeader__content">
                      {{ t(`pages.CompaniesFoodMenus.edit.category.${category}`) }}
                    </h2>
                    <KkTooltip
                      :label="t('pages.CompaniesFoodMenus.edit.category.tooltip.label')"
                      top="25px"
                      left="-25px"
                      width="400px"
                      :is-visible="showCategoryToolTips.includes(category)"
                      @showTooltip="showCategoryToolTips.push(category)"
                      @hideTooltip="showCategoryToolTips = showCategoryToolTips.filter(c => c !== category)"
                    >
                      <p class="whiteSpacePreWrap">
                        {{ categoryTooltipText }}
                      </p>
                      <div v-show="categoryAvailableSelectedServices(category).length" class="siteIcons">
                        <div v-for="site in categoryAvailableSelectedServices(category)" :key="site">
                          <KkSiteIcon class="siteIcons__icon" :name="site" />
                        </div>
                      </div>
                    </KkTooltip>
                  </div>
                  <div v-for="section in categorySections(category)" :key="section.key" class="p-box foodMenuSection">
                    <div class="p-box__header">
                      <div class="foodMenuSection__container">
                        <div class="foodMenuSection__title">
                          <div class="p-box__title">{{ section.label }}</div>
                          <div class="foodMenuSection__description">{{ section.description }}</div>
                        </div>
                        <div class="foodMenuSection__editButton">
                          <span @click="openDropdown(section.key)">
                            <KkIcon name="iconVerticalThreePointLeader" class="icon" />
                          </span>
                          <div v-if="activeDropDown === section.key">
                            <ul class="foodMenuSection__editList">
                              <li class="foodMenuSection__editListItem" @click="openEditSection(section, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.edit') }}</li>
                              <li class="foodMenuSection__editListItem" @click="handleMoveSectionTop(section.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveTop') }}</li>
                              <li class="foodMenuSection__editListItem" @click="handleMoveSectionOneUp(section.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveOneUp') }}</li>
                              <li class="foodMenuSection__editListItem" @click="handleMoveSectionOneDown(section.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveOneDown') }}</li>
                              <li class="foodMenuSection__editListItem" @click="handleMoveSectionBottom(section.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveBottom') }}</li>
                              <li class="foodMenuItem__editListItem" @click="handleDeleteSection(section.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.delete') }}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <div class="foodMenuSection__container">
                        <div v-if="canSectionEditCategories.includes(category) && activeEdit === section.key" class="foodMenuSection__content">
                          <ValidationObserver v-slot="{ valid }">
                            <fieldset v-if="showSectionLabelForm" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  v-model="categoryFormValues[category].label"
                                                  :name="t('pages.CompaniesFoodMenus.edit.section.label.name')"
                                                  :rules="sectionLabelRules"
                              >
                                <div class="labelBox">
                                  <KkFormLabel
                                    :text="sectionLabelFormLabel(category)"
                                  />
                                  <KkTooltip
                                    :label="t('pages.CompaniesFoodMenus.edit.section.label.tooltip.label')"
                                    top="25px"
                                    left="-25px"
                                    width="400px"
                                    :is-visible="showSectionLabelTooltip"
                                    @showTooltip="showSectionLabelTooltip = true"
                                    @hideTooltip="showSectionLabelTooltip = false"
                                  >
                                    <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.title') }}</p>
                                    <p>
                                      {{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.description') }}
                                    </p>
                                    <div v-show="sectionLabelAvailableSelectedServices.length" class="siteIcons">
                                      <div v-for="site in sectionLabelAvailableSelectedServices" :key="site">
                                        <KkSiteIcon :name="site" class="siteIcons__icon" />
                                      </div>
                                    </div>
                                  </KkTooltip>
                                </div>
                                <TextField
                                  :value="value"
                                  :maxlength="sectionLabelMaxLength"
                                  :placeholder="t('pages.CompaniesFoodMenus.edit.section.placeHolder.label')"
                                  @input="handleChange"
                                />
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                            <span v-if="showSectionLabelForm" class="foodMenu__example">{{ t('pages.CompaniesFoodMenus.edit.section.example') }}</span>
                            <fieldset v-if="showSectionDescriptionForm" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12 foodMenuSection__editDescription">
                              <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].description" :name="t('pages.CompaniesFoodMenus.edit.section.description') " :rules="sectionDescriptionRules">
                                <label class="c-formLabel">
                                  {{ t('pages.CompaniesFoodMenus.edit.section.description') }}
                                  <span>({{ t('labels.countCharacters', { count: categoryFormValues[category].description.length, max: 100 }) }}){{ t('labels.optionalLabel') }}</span>
                                </label>
                                <TextField
                                  :value="value"
                                  maxlength="100"
                                  :placeholder="t('pages.CompaniesFoodMenus.edit.section.placeHolder.description')"
                                  @input="handleChange"
                                />
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                            <div class="c-button">
                              <button
                                type="button"
                                class="c-button__item c-button__item--outline-cancel"
                                @click="initialEdit(category)"
                              >
                                {{ t('labels.cancel') }}
                              </button>
                              <button
                                type="button"
                                class="c-button__item c-button__item--primary"
                                :disabled="!valid"
                                @click="handleUpdateSection(section.key, category)"
                              >
                                {{ t('labels.confirm') }}
                              </button>
                            </div>
                          </ValidationObserver>
                        </div>
                      </div>
                    </div>
                    <div
                      v-for="item in section.items"
                      :key="item.key"
                      class="p-box foodMenuItem"
                    >
                      <div class="p-box__body">
                        <div class="foodMenuItem__container">
                          <div v-if="item.imageUrl" class="foodMenuItem__image">
                            <img :src="item.imageUrl" :alt="item.filename">
                          </div>
                          <div class="foodMenuItem__content">
                            <div class="foodMenuItem__content--above">
                              <div class="foodMenuItem__name">
                                <span v-if="item.isRecommend" class="recommendBadge">
                                  {{ t('pages.CompaniesFoodMenus.edit.item.recommend.badge') }}
                                </span>
                                {{ item.label }}
                              </div>
                              <div v-if="item.price" class="foodMenuItem__place">
                                {{ t('labels.currency.yen', { number: numberLocaleString(item.price) }) }}
                              </div>
                            </div>
                            <div class="foodMenuItem__content--bottom">{{ item.description }}</div>
                          </div>
                          <div class="foodMenuItem__editButton">
                            <span @click="openDropdown(item.key)"><KkIcon name="iconVerticalThreePointLeader" class="icon" /></span>
                            <div v-if="activeDropDown === item.key">
                              <ul class="foodMenuItem__editList">
                                <li class="foodMenuItem__editListItem" @click="openEditItem(item, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.edit') }}</li>
                                <li class="foodMenuItem__editListItem" @click="handleMoveItemTop(section.key, item.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveTop') }}</li>
                                <li class="foodMenuItem__editListItem" @click="handleMoveItemOneUp(section.key, item.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveOneUp') }}</li>
                                <li class="foodMenuItem__editListItem" @click="handleMoveItemOneDown(section.key, item.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveOneDown') }}</li>
                                <li class="foodMenuItem__editListItem" @click="handleMoveItemBottom(section.key, item.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.moveBottom') }}</li>
                                <li class="foodMenuItem__editListItem" @click="handleDeleteItem(section.key, item.key, category)">{{ t('pages.CompaniesFoodMenus.edit.labels.delete') }}</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="canItemEditCategories.includes(category) && activeEdit === item.key">
                        <div class="p-box__body">
                          <ValidationObserver v-slot="{ valid }">
                            <template v-if="showImageForm">
                              <MediaImage
                                :images="images(category)"
                                :loading="loading"
                                :label-detail="t(`labels.${itemImageRequired(categoryFormValues[category].item) ? 'requiredLabel' : 'optionalLabel'}`)"
                                :max-files="1"
                                rules="image|postImage|size:5000|ext:jpeg,jpg,png"
                                @drop-images="(files: File[]) => onDropMediaImage(files, category)"
                                @remove-image="onRemoveMediaImage(category)"
                              >
                                <template #labelContent>
                                  <span class="imageTooltip">
                                    <KkTooltip
                                      :label="t('pages.CompaniesFoodMenus.edit.item.image.tooltip.label')"
                                      top="25px"
                                      left="-25px"
                                      width="400px"
                                      :is-visible="showImageTooltip"
                                      @show-tooltip="showImageTooltip = true"
                                      @hide-tooltip="showImageTooltip = false"
                                    >
                                      <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.title') }}</p>
                                      <p class="whiteSpacePreWrap">
                                        {{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.description') }}
                                      </p>
                                      <div class="siteIcons">
                                        <div v-for="site in imageAvailableSelectedServices" :key="site">
                                          <KkSiteIcon :name="site" class="siteIcons__icon" />
                                        </div>
                                      </div>
                                      <KkImageTooltipContent
                                        count="1"
                                        format="JPG,JPEG,PNG"
                                        :width="availableImageSize"
                                        :height="availableImageSize"
                                        size="5"
                                        :is-visible-resize="true"
                                        :is-visible-recommend-description="true"
                                      />
                                    </KkTooltip>
                                  </span>
                                </template>
                              </MediaImage>
                            </template>
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.label" :name="t('pages.CompaniesFoodMenus.edit.item.label') " :rules="itemLabelRules">
                                <label class="c-formLabel">
                                  {{ t('pages.CompaniesFoodMenus.edit.item.label') }}
                                  <span>
                                    ({{ t('labels.countCharacters', { count: value.length, max: itemLabelMaxLength }) }})
                                    {{ t('labels.requiredLabel') }}
                                  </span>
                                  <span class="c-error-msg">{{ errors[0] }}</span>
                                </label>
                                <TextField
                                  :value="value"
                                  :maxlength="itemLabelMaxLength"
                                  :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.label')"
                                  @input="handleChange"
                                />
                                <p v-if="selectedServiceTypes.includes('yahoo_place')" class="item__annotation">{{ $t('pages.CompaniesFoodMenus.edit.item.yahooMapAnnotation') }}</p>
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                            <fieldset v-if="recommendAvailableSelectedServices.length" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <div class="labelBox">
                                <KkFormLabel
                                  :text="t('pages.CompaniesFoodMenus.edit.item.recommend.text') + t('labels.optionalLabel')"
                                />
                                <KkTooltip
                                  :label="t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.label')"
                                  top="25px"
                                  left="-25px"
                                  width="400px"
                                  :is-visible="showRecommendTooltip"
                                  @showTooltip="showRecommendTooltip = true"
                                  @hideTooltip="showRecommendTooltip = false"
                                >
                                  <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.title') }}</p>
                                  <p>
                                    {{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.description') }}
                                  </p>
                                  <div class="siteIcons">
                                    <div v-for="site in recommendAvailableSelectedServices" :key="site">
                                      <KkSiteIcon :name="site" class="siteIcons__icon" />
                                    </div>
                                  </div>
                                </KkTooltip>
                              </div>
                              <KkCheckbox
                                :id="`itemEdit-${section.key}`"
                                :label="t('pages.CompaniesFoodMenus.edit.item.recommend.label')"
                                :model-value="categoryFormValues[category].item.recommend"
                                :is-disabled="invalidRecommendItemEdit"
                                @update:model-value="(value: boolean) => categoryFormValues[category].item.recommend = value"
                              />
                            </fieldset>
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  v-model="categoryFormValues[category].item.price"
                                                  :name="t('pages.CompaniesFoodMenus.edit.item.price.label')"
                                                  :rules="itemPriceRules"
                              >
                                <KkFormLabel
                                  :text="itemPriceLabel(value, category)"
                                />
                                <KkTooltip
                                  v-if="selectedTabelog"
                                  :label="t('pages.CompaniesFoodMenus.edit.item.price.text')"
                                  top="25px"
                                  left="-25px"
                                  width="400px"
                                  :is-visible="showPriceTooltip"
                                  @showTooltip="showPriceTooltip = true"
                                  @hideTooltip="showPriceTooltip = false"
                                >
                                  <p>
                                    {{ t('pages.CompaniesFoodMenus.edit.item.price.tooltip.description') }}
                                  </p>
                                </KkTooltip>
                                <input
                                  :value="value"
                                  type="number"
                                  class="c-textField"
                                  :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.price')"
                                  @wheel="$event.target && ($event.target as HTMLInputElement).blur()"
                                  @input="handleChange(handlePriceChange($event))"
                                >
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.description" :name="t('pages.CompaniesFoodMenus.edit.item.description') " :rules="itemDescriptionRules">
                                <label class="c-formLabel">
                                  {{ t('pages.CompaniesFoodMenus.edit.item.description') }}
                                  <span>({{ t('labels.countCharacters', { count: value.length, max: itemDescriptionMaxLength(categoryFormValues[category].item) }) }}){{ t(`labels.${itemDescriptionRequired(categoryFormValues[category].item) ? "requiredLabel" : "optionalLabel"}`) }}</span>
                                </label>
                                <textarea
                                  :value="value"
                                  class="c-textArea"
                                  :maxlength="itemDescriptionMaxLength(categoryFormValues[category].item)"
                                  :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.description')"
                                  @input="handleChange"
                                />
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                            <div class="c-button">
                              <button
                                type="button"
                                class="c-button__item c-button__item--outline-cancel"
                                @click="initialEdit(category)"
                              >
                                {{ t('labels.cancel') }}
                              </button>
                              <button type="button" class="c-button__item c-button__item--primary" :disabled="!isValidItem(valid, categoryFormValues[category].item)" @click="handleUpdateItem(section.key, item.key, category)">
                                {{ t('labels.confirm') }}
                              </button>
                            </div>
                          </ValidationObserver>
                        </div>
                      </div>
                    </div>
                    <div v-if="canItemAddCategories.includes(category) && activeEdit === section.key">
                      <div class="p-box__body">
                        <ValidationObserver v-slot="{ valid }">
                          <template v-if="showImageForm">
                            <MediaImage
                              :images="images(category)"
                              :loading="loading"
                              :label-detail="t(`labels.${itemImageRequired(categoryFormValues[category].item) ? 'requiredLabel' : 'optionalLabel'}`)"
                              :max-files="1"
                              rules="image|postImage|size:5000|ext:jpeg,jpg,png"
                              @drop-images="(files: File[]) => onDropMediaImage(files, category)"
                              @remove-image="onRemoveMediaImage(category)"
                            >
                              <template #labelContent>
                                <span class="imageTooltip">
                                  <KkTooltip
                                    :label="t('pages.CompaniesFoodMenus.edit.item.image.tooltip.label')"
                                    top="25px"
                                    left="-25px"
                                    width="400px"
                                    :is-visible="showImageTooltip"
                                    @show-tooltip="showImageTooltip = true"
                                    @hide-tooltip="showImageTooltip = false"
                                  >
                                    <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.title') }}</p>
                                    <p class="whiteSpacePreWrap">
                                      {{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.description') }}
                                    </p>
                                    <div class="siteIcons">
                                      <div v-for="site in imageAvailableSelectedServices" :key="site">
                                        <KkSiteIcon :name="site" class="siteIcons__icon" />
                                      </div>
                                    </div>
                                    <KkImageTooltipContent
                                      count="1"
                                      format="JPG,JPEG,PNG"
                                      :width="availableImageSize"
                                      :height="availableImageSize"
                                      size="5"
                                      :is-visible-resize="true"
                                      :is-visible-recommend-description="true"
                                    />
                                  </KkTooltip>
                                </span>
                              </template>
                            </MediaImage>
                          </template>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.label" :name="t('pages.CompaniesFoodMenus.edit.item.label') " :rules="itemLabelRules">
                              <label class="c-formLabel">
                                {{ t('pages.CompaniesFoodMenus.edit.item.label') }}
                                <span>
                                  ({{ t('labels.countCharacters', { count: value.length, max: itemLabelMaxLength }) }})
                                  {{ t('labels.requiredLabel') }}
                                </span>
                                <span class="c-error-msg">{{ errors[0] }}</span>
                              </label>
                              <TextField
                                :value="value"
                                :maxlength="itemLabelMaxLength"
                                :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.label')"
                                @input="handleChange"
                              />
                              <p v-if="selectedServiceTypes.includes('yahoo_place')" class="item__annotation">{{ $t('pages.CompaniesFoodMenus.edit.item.yahooMapAnnotation') }}</p>
                            </ValidationProvider>
                          </fieldset>
                          <fieldset v-if="recommendAvailableSelectedServices.length" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <div class="labelBox">
                              <KkFormLabel
                                :text="t('pages.CompaniesFoodMenus.edit.item.recommend.text') + t('labels.optionalLabel')"
                              />
                              <KkTooltip
                                :label="t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.label')"
                                top="25px"
                                left="-25px"
                                width="400px"
                                :is-visible="showRecommendTooltip"
                                @showTooltip="showRecommendTooltip = true"
                                @hideTooltip="showRecommendTooltip = false"
                              >
                                <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.title') }}</p>
                                <p>
                                  {{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.description') }}
                                </p>
                                <div class="siteIcons">
                                  <div v-for="site in recommendAvailableSelectedServices" :key="site">
                                    <KkSiteIcon :name="site" class="siteIcons__icon" />
                                  </div>
                                </div>
                              </KkTooltip>
                            </div>
                            <KkCheckbox
                              :id="`addItem-${section.key}`"
                              :label="t('pages.CompaniesFoodMenus.edit.item.recommend.label')"
                              :model-value="categoryFormValues[category].item.recommend"
                              :is-disabled="invalidRecommendItemEdit"
                              @update:model-value="(value: boolean) => categoryFormValues[category].item.recommend = value"
                            />
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                v-model="categoryFormValues[category].item.price"
                                                :name="t('pages.CompaniesFoodMenus.edit.item.price.label')"
                                                :rules="itemPriceRules"
                            >
                              <KkFormLabel
                                :text="itemPriceLabel(value, category)"
                              />
                              <KkTooltip
                                v-if="selectedTabelog"
                                :label="t('pages.CompaniesFoodMenus.edit.item.price.text')"
                                top="25px"
                                left="-25px"
                                width="400px"
                                :is-visible="showPriceTooltip"
                                @showTooltip="showPriceTooltip = true"
                                @hideTooltip="showPriceTooltip = false"
                              >
                                <p>
                                  {{ t('pages.CompaniesFoodMenus.edit.item.price.tooltip.description') }}
                                </p>
                              </KkTooltip>
                              <input
                                :value="value"
                                type="number"
                                class="c-textField"
                                :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.price')"
                                @wheel="$event.target && ($event.target as HTMLInputElement).blur()"
                                @input="handleChange(handlePriceChange($event))"
                              >
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.description" :name="t('pages.CompaniesFoodMenus.edit.item.description') " :rules="itemDescriptionRules">
                              <label class="c-formLabel">
                                {{ t('pages.CompaniesFoodMenus.edit.item.description') }}
                                <span>({{ t('labels.countCharacters', { count: value.length, max: itemDescriptionMaxLength(categoryFormValues[category].item) }) }}){{ t(`labels.${itemDescriptionRequired(categoryFormValues[category].item) ? "requiredLabel" : "optionalLabel"}`) }}</span>
                              </label>
                              <textarea
                                :value="value"
                                class="c-textArea"
                                :maxlength="itemDescriptionMaxLength(categoryFormValues[category].item)"
                                :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.description')"
                                @input="handleChange"
                              />
                              <ValidationError :error="errors[0]" />
                            </ValidationProvider>
                          </fieldset>
                          <div class="c-button">
                            <button
                              type="button"
                              class="c-button__item c-button__item--outline-cancel"
                              @click="initialEdit(category)"
                            >
                              {{ t('labels.cancel') }}
                            </button>
                            <button type="button" class="c-button__item c-button__item--primary" :disabled="!isValidItem(valid, categoryFormValues[category].item)" @click="handleAddItem(section.key, category)">
                              {{ t('labels.confirm') }}
                            </button>
                          </div>
                        </ValidationObserver>
                      </div>
                    </div>
                    <div v-else>
                      <button type="button" class="foodMenuItem__add" @click="openAddItem(section.key, category)">
                        <KkIcon class="foodMenuItem__addImage" name="iconPlusRed" />
                        <span class="foodMenuItem__addText">{{ t('pages.CompaniesFoodMenus.edit.item.add') }}</span>
                      </button>
                    </div>
                  </div>
                  <div v-if="canSectionAddCategories.includes(category)" class="addSectionForm">
                    <ValidationObserver v-slot="{ valid }">
                      <div>
                        <fieldset v-if="showSectionLabelForm" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ errors, value, handleChange }"
                                              v-model="categoryFormValues[category].label"
                                              :name="t('pages.CompaniesFoodMenus.edit.section.label.name')"
                                              :rules="sectionLabelRules"
                          >
                            <div class="labelBox">
                              <KkFormLabel
                                :text="sectionLabelFormLabel(category)"
                              />
                              <KkTooltip
                                :label="t('pages.CompaniesFoodMenus.edit.section.label.tooltip.label')"
                                top="25px"
                                left="-25px"
                                width="400px"
                                :is-visible="showSectionLabelTooltip"
                                @showTooltip="showSectionLabelTooltip = true"
                                @hideTooltip="showSectionLabelTooltip = false"
                              >
                                <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.title') }}</p>
                                <p>
                                  {{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.description') }}
                                </p>
                                <div v-show="sectionLabelAvailableSelectedServices.length" class="siteIcons">
                                  <div v-for="site in sectionLabelAvailableSelectedServices" :key="site">
                                    <KkSiteIcon :name="site" class="siteIcons__icon" />
                                  </div>
                                </div>
                              </KkTooltip>
                            </div>
                            <TextField
                              :value="value"
                              :maxlength="sectionLabelMaxLength"
                              :placeholder="t('pages.CompaniesFoodMenus.edit.section.placeHolder.label')"
                              @input="handleChange"
                            />
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <span v-if="showSectionLabelForm" class="foodMenu__example">{{ t('pages.CompaniesFoodMenus.edit.section.example') }}</span>
                        <fieldset v-if="showSectionDescriptionForm" class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider
                            v-slot="{ errors, value, handleChange }"
                            v-model="categoryFormValues[category].description"
                            :name="t('pages.CompaniesFoodMenus.edit.section.description')"
                            :rules="sectionDescriptionRules"
                          >
                            <div class="labelBox">
                              <KkFormLabel
                                :text="t('pages.CompaniesFoodMenus.edit.section.description') + '(' + t('labels.countCharacters', { count: categoryFormValues[category].description.length, max: 100 }) + ')' + t('labels.optionalLabel')"
                              />
                              <KkTooltip
                                top="25px"
                                left="-25px"
                                width="400px"
                                :label="t('pages.CompaniesFoodMenus.edit.section.label.tooltip.label')"
                                :is-visible="showSectionDescriptionTooltip"
                                @showTooltip="showSectionDescriptionTooltip = true"
                                @hideTooltip="showSectionDescriptionTooltip = false"
                              >
                                <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.title') }}</p>
                                <p>
                                  {{ t('pages.CompaniesFoodMenus.edit.section.label.tooltip.description') }}
                                </p>
                                <div v-show="sectionDescriptionAvailableSelectedServices.length" class="siteIcons">
                                  <div v-for="site in sectionDescriptionAvailableSelectedServices" :key="site">
                                    <KkSiteIcon :name="site" class="siteIcons__icon" />
                                  </div>
                                </div>
                              </KkTooltip>
                            </div>
                            <TextField
                              :value="value"
                              maxlength="100"
                              :placeholder="t('pages.CompaniesFoodMenus.edit.section.placeHolder.description')"
                              @input="handleChange"
                            />
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                      </div>
                      <div class="item">
                        <h3 class="item__title">
                          {{ t('pages.CompaniesFoodMenus.edit.item.add') }}
                        </h3>
                        <KkInfo class="item__info">
                          <p>{{ t('pages.CompaniesFoodMenus.edit.item.description1') }}</p>
                        </KkInfo>
                        <template v-if="showImageForm">
                          <MediaImage
                            :images="images(category)"
                            :loading="loading"
                            :label-detail="t(`labels.${itemImageRequired(categoryFormValues[category].item) ? 'requiredLabel' : 'optionalLabel'}`)"
                            :max-files="1"
                            rules="image|postImage|size:5000|ext:jpeg,jpg,png"
                            @drop-images="(files: File[]) => onDropMediaImage(files, category)"
                            @remove-image="onRemoveMediaImage(category)"
                          >
                            <template #labelContent>
                              <span class="imageTooltip">
                                <KkTooltip
                                  :label="t('pages.CompaniesFoodMenus.edit.item.image.tooltip.label')"
                                  top="25px"
                                  left="-25px"
                                  width="400px"
                                  :is-visible="showImageTooltip"
                                  @show-tooltip="showImageTooltip = true"
                                  @hide-tooltip="showImageTooltip = false"
                                >
                                  <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.title') }}</p>
                                  <p class="whiteSpacePreWrap">
                                    {{ t('pages.CompaniesFoodMenus.edit.item.image.tooltip.description') }}
                                  </p>
                                  <div class="siteIcons">
                                    <div v-for="site in imageAvailableSelectedServices" :key="site">
                                      <KkSiteIcon :name="site" class="siteIcons__icon" />
                                    </div>
                                  </div>
                                  <KkImageTooltipContent
                                    count="1"
                                    format="JPG,JPEG,PNG"
                                    :width="availableImageSize"
                                    :height="availableImageSize"
                                    size="5"
                                    :is-visible-resize="true"
                                    :is-visible-recommend-description="true"
                                  />
                                </KkTooltip>
                              </span>
                            </template>
                          </MediaImage>
                        </template>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.label" :name="t('pages.CompaniesFoodMenus.edit.item.label') " :rules="itemLabelRules">
                            <label class="c-formLabel">
                              {{ t('pages.CompaniesFoodMenus.edit.item.label') }}
                              <span>
                                ({{ t('labels.countCharacters', { count: value.length, max: itemLabelMaxLength }) }})
                                {{ t('labels.requiredLabel') }}
                              </span>
                              <span class="c-error-msg">{{ errors[0] }}</span>
                            </label>
                            <TextField
                              :value="value"
                              :maxlength="itemLabelMaxLength"
                              :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.label')"
                              @input="handleChange"
                            />
                            <p v-if="selectedServiceTypes.includes('yahoo_place')" class="item__annotation">{{ $t('pages.CompaniesFoodMenus.edit.item.yahooMapAnnotation') }}</p>
                          </ValidationProvider>
                        </fieldset>
                        <fieldset v-if="recommendAvailableSelectedServices.length" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <div class="labelBox">
                            <KkFormLabel
                              :text="t('pages.CompaniesFoodMenus.edit.item.recommend.text') + t('labels.optionalLabel')"
                            />
                            <KkTooltip
                              :label="t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.label')"
                              top="25px"
                              left="-25px"
                              width="400px"
                              :is-visible="showRecommendTooltip"
                              @showTooltip="showRecommendTooltip = true"
                              @hideTooltip="showRecommendTooltip = false"
                            >
                              <p class="tooltipTitle">{{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.title') }}</p>
                              <p>
                                {{ t('pages.CompaniesFoodMenus.edit.item.recommend.tooltip.description') }}
                              </p>
                              <div class="siteIcons">
                                <div v-for="site in recommendAvailableSelectedServices" :key="site">
                                  <KkSiteIcon :name="site" class="siteIcons__icon" />
                                </div>
                              </div>
                            </KkTooltip>
                          </div>
                          <KkCheckbox
                            id="addSection"
                            :label="t('pages.CompaniesFoodMenus.edit.item.recommend.label')"
                            :model-value="categoryFormValues[category].item.recommend"
                            :is-disabled="invalidRecommendItemEdit"
                            @update:model-value="(value: boolean) => categoryFormValues[category].item.recommend = value"
                          />
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ errors, value, handleChange }"
                                              v-model="categoryFormValues[category].item.price"
                                              :name="t('pages.CompaniesFoodMenus.edit.item.price.label')"
                                              :rules="itemPriceRules"
                          >
                            <KkFormLabel
                              :text="itemPriceLabel(value, category)"
                            />
                            <KkTooltip
                              v-if="selectedTabelog"
                              :label="t('pages.CompaniesFoodMenus.edit.item.price.text')"
                              top="25px"
                              left="-25px"
                              width="400px"
                              :is-visible="showPriceTooltip"
                              @showTooltip="showPriceTooltip = true"
                              @hideTooltip="showPriceTooltip = false"
                            >
                              <p>
                                {{ t('pages.CompaniesFoodMenus.edit.item.price.tooltip.description') }}
                              </p>
                            </KkTooltip>
                            <input
                              :value="value"
                              type="number"
                              class="c-textField"
                              :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.price')"
                              @wheel="$event.target && ($event.target as HTMLInputElement).blur()"
                              @input="handleChange(handlePriceChange($event))"
                            >
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                          <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="categoryFormValues[category].item.description" :name="t('pages.CompaniesFoodMenus.edit.item.description') " :rules="itemDescriptionRules">
                            <label class="c-formLabel">
                              {{ t('pages.CompaniesFoodMenus.edit.item.description') }}
                              <span>({{ t('labels.countCharacters', { count: value.length, max: itemDescriptionMaxLength(categoryFormValues[category].item) }) }}){{ t(`labels.${itemDescriptionRequired(categoryFormValues[category].item) ? "requiredLabel" : "optionalLabel"}`) }}</span>
                            </label>
                            <textarea
                              :value="value"
                              class="c-textArea"
                              :maxlength="itemDescriptionMaxLength(categoryFormValues[category].item)"
                              :placeholder="t('pages.CompaniesFoodMenus.edit.item.placeHolder.description')"
                              @input="handleChange"
                            />
                            <ValidationError :error="errors[0]" />
                          </ValidationProvider>
                        </fieldset>
                        <div class="c-button">
                          <button
                            type="button"
                            class="c-button__item c-button__item--outline-cancel"
                            @click="initialEdit(category)"
                          >
                            {{ t('labels.cancel') }}
                          </button>
                          <button type="button" class="c-button__item c-button__item--primary" :disabled="!valid" @click="handleAddSection(category)">
                            {{ t('labels.confirm') }}
                          </button>
                        </div>
                      </div>
                    </ValidationObserver>
                  </div>
                  <template v-else>
                    <p v-if="categorySections(category).length === 0">{{ t('messages.noSettings') }}</p>
                    <div class="addSection" @click="openAddSection(category)">
                      <div class="addSection__content">
                        <div class="addSection__icon">
                          <KkIcon name="iconPlusRed" />
                        </div>
                        <p class="addSection__btn">
                          {{ t('pages.CompaniesFoodMenus.edit.section.add') }}
                        </p>
                      </div>
                    </div>
                  </template>
                </template>
              </KkBox>
            </div>
          </section>
          <section class="c-grid__item c-grid__item__s__12 c-grid__item__m__4 c-grid__item__l__4">
            <div class="stickyArea">
              <KkSelectStore
                :target-store-label="t('pages.CompaniesFoodMenus.pleaseSelectStores')"
                :selected-store-count="storeUuids.length"
                :disabled="searchAttributesLoading || searchStoresLoading"
                @open-store-modal="handleOpenStoreModal"
              >
                <div class="selectStore__info">
                  <KkInfo>{{ t('pages.CompaniesFoodMenus.noticeManyStoresMenu') }}</KkInfo>
                </div>
                <div class="selectStore__buttons">
                  <KkButton :text="t('labels.cancel')"
                            color="secondary"
                            @click="cancelPage"
                  />
                  <KkButton :text="t('labels.save')"
                            :disabled="invalidFoodMenu"
                            @click="openPreviewModal"
                  />
                </div>
              </KkSelectStore>
              <KkItemCounter
                v-if="showItemCounter"
                :food-menu="foodMenu"
                :selected-service-types="selectedServiceTypes"
              />
              <p v-show="selectedServiceTypes.includes('yahoo_place')" class="yahooMapNotice">
                {{ t('pages.CompaniesFoodMenus.edit.foodMenu.yahooMapNotice') }}
              </p>
            </div>
          </section>
        </div>
      </div>
      <KkMultiServicePreviewModal
        v-if="showPreviewModal"
        :is-preview="false"
        :is-visible="showPreviewModal"
        :title="t('pages.CompaniesFoodMenus.edit.preview.title')"
        :sections="foodMenuSections"
        :store-names="storeNames"
        :selected-services="selectedServiceTypes"
        @submit="saveFoodMenus"
        @close="showPreviewModal = false"
      />
    </div>
    <KkLoading v-show="loading" />
    <div v-if="activeDropDown !== ''" class="foodMenu__editBg" @click="closeDropdown()" />
    <KkFilterModal
      :is-visible="visibleFilterModal"
      :filter-items="filterItems"
      :form-values="filterFormValues"
      :total-count="filteredCount"
      :submit-button-text="t('labels.checkTargetStores')"
      @close="handleCloseFilterModal"
      @submit="handleSubmitFilterModal"
      @reset="handleResetFilterModal"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
    <KkStoreModal
      v-if="showStoreModal"
      store-name="companiesFoodMenu"
      :only-gmb-connected="false"
      :search-stores="stores"
      :form-values="foodMenusFormValues"
      :store-total-count="storeTotalCount"
      @close-store-modal="confirmStoreUuids"
      @back-filter-modal="handleBack()"
      @set-form-values="setFoodMenuFormValues"
      @toggle-filter-values="toggleFormValue"
      @set-filter-values="setFoodMenuFilterValues"
    />
  </div>
</template>


<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';
.foodMenu{
  &__title {
    margin-bottom: 20px;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.5;
  }

  &__example {
    font-size: 12px;
  }

  &__editBg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.item {
  margin-top: 24px;
  &__title {
    margin-bottom: 16px;
  }
  &__info {
    margin-bottom: 16px;
  }
  &__annotation {
    font-size: 12px;
  }
}

.foodMenuSection {
  border-radius: 4px;
  box-shadow: 0 0 0 rgb(63 63 68 / 5%);
  background: #fff;
  word-wrap: break-word;

  &__title {
    padding-right: 30px;
    width: 100%;
  }

  &__container {
    width: 100%;
    display: flex;
  }

  &__description {
    color: #9c9c9c;
  }

  &__editDescription {
    margin-top: 28px;
  }

  &__content {
    width: 100%;
    padding: 40px 0 20px
  }

  &__editButton {
    display: flex;
    margin-left: auto;
    cursor: pointer;
    position: relative;
    align-items: center;
  }

  &__editList {
    position: absolute;
    width: 150px;
    background: white;
    box-shadow: 0 1px 4px rgb(0 0 0 / 26%);
    padding: 10px 0;
    z-index: 1;
  }

  &__editListItem{
    margin-bottom: 5px;
    padding: 0 10px;
    &:hover {
      background-color: rgba(0,0,0,.1);
      cursor: pointer;
    }
  }
}

.foodMenuItem {
  width: 90%;
  margin-right: auto;
  margin-left: auto;
  word-wrap: break-word;

  &__container{
    display: flex;
    justify-content: space-between;
  }

  &__recommendDescription{
    margin-top: 10px;
  }

  &__content {
    width: 100%;
    padding-right: 30px;

    &--above {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;
      font-weight: 700;
    }

    &--bottom {
      color: #9c9c9c;
    }
  }

  &__name {
    word-break: break-all;
  }
  &__place {
    margin-left: 16px;
    white-space: nowrap;
  }

  &__editButton {
    display: flex;
    align-items: center;
    margin-left: auto;
    cursor: pointer;
    position: relative;
  }

  &__editList {
    position: absolute;
    width: 150px;
    background: white;
    box-shadow: 0 1px 4px rgb(0 0 0 / 26%);
    padding: 10px 0;
    z-index: 1;
  }

  &__editListItem{
    margin-bottom: 5px;
    padding: 0 10px;
    &:hover {
      background-color: rgba(0,0,0,.1);
      cursor: pointer;
    }
  }

  &__add {
    display: flex;
    align-items: center;
    width: 90%;
    margin: 15px auto;
    cursor: pointer;
  }

  &__addImage {
    width: 16px;
    height: 16px;
  }

  &__addText {
    color: #ec5b7d;
    margin-left: 5px;
    font-size: 12px;
  }

  &__image {
    display: block;
    width: 20%;
    height: 100%;
    margin-right: 8px;
    object-fit: cover;
    border-radius: 4px;
  }
}
.foodMenuIndexLink {
  margin-left: auto;
}

.addSection {
  margin-top: 24px;
  display: flex;
  align-content: center;
  height: 150px;
  background: $color-base-i;
  border: 1px solid $color-base-i;
  border-radius: 4px;

  &__content {
    margin: auto;
  }

  &__icon {
    margin: 0 auto;
    width: 32px;
    height:32px;
  }

  &__btn {
    margin-top: 5px;
    color: #ec5b7d;
    text-align: center;
  }

  &:hover {
    cursor: pointer;
  }
}

.c-button__item:disabled {
  cursor: auto;
  opacity: 0.3;
}

.selectStore {
  &__info {
    margin-top: 16px;
  }

  &__buttons {
    display: flex;
    align-items: center;
    margin: 32px -8px 0 -8px;

    button {
      flex-grow: 1;
      margin: 0 8px 8px;
    }
  }
}
.imageAdd {
  &__icon {
    width: 16px;
    margin-right: 8px;

  }
}

.recommendBadge {
  background: #393a3e;
  border-radius: 12px;
  font-weight: bold;
  color: #FFF;
  font-size: 12px;
  padding: 4px 6px;
}
.selectServices {
  margin-top: 16px;
  &__form {
    margin-top: 16px;
  }
}
.serviceCheckbox {
  margin-top: 8px;
  display: flex;
  gap: 24px;
}
.categories {
  margin-top: 16px;
  display: grid;
  grid-auto-columns: 100%;
  justify-content: stretch;
  gap: 16px;
}
.categoryHeader {
  margin-bottom: 16px;
  display: flex;
  place-items: center;
  gap: 8px;
  &__content {
    font-weight: bold;
    font-size: 20px;
    line-height: 1.25;
  }
}
.whiteSpacePreWrap {
  white-space: pre-wrap;
}
.tooltipTitle {
  font-weight: bold;
  margin-bottom: 8px;
}
.siteIcons {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  &__icon {
    width: 32px;
  }
}
.labelBox {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.icon {
  width: 16px;
}
.addSectionForm {
  margin-top: 24px;
}
.imageTooltip {
  font-weight: normal;
}
.stickyArea {
  position: sticky;
  top: 30px;
}
.yahooMapNotice {
  margin-top: 16px;
  color: #ec5b7d;
  font-size: 12px;
}
</style>
